import type {
    EBirdCalibratedObservation,
    EBirdMyDataSchema,
    EBirdObservationsBySpecies,
    EBirdSightingsCalibrationResult
} from "ebird-mydata-reader";

type CalibrationScope = number | 'life';

export function getObservationCalibration(
    observation: EBirdMyDataSchema,
    scope: CalibrationScope = 'life'
) {
    const calibratedObservation = observation as EBirdCalibratedObservation;

    if (scope === 'life') {
        return calibratedObservation.calibration?.life;
    }

    return calibratedObservation.calibration?.years?.[scope.toString()];
}

export function isCountableCalibrationRepresentative(
    observation: EBirdMyDataSchema,
    scope: CalibrationScope = 'life'
) {
    const calibration = getObservationCalibration(observation, scope);

    return !calibration || calibration.countableRepresentative;
}

function isExactCalibrationListEntry(observation: EBirdMyDataSchema, scope: CalibrationScope = 'life') {
    const calibration = getObservationCalibration(observation, scope);
    const listEntry = calibration?.listEntry;
    const observationSubmissionId = observation.submissionId ?? observation.submissionID;

    return Boolean(
        listEntry
        && observationSubmissionId
        && listEntry.submissionId === observationSubmissionId
    );
}

export function isUncountableCalibrationObservation(
    observation: EBirdMyDataSchema,
    scope: CalibrationScope = 'life'
) {
    const calibration = getObservationCalibration(observation, scope);

    if (!calibration) return false;
    if (!isExactCalibrationListEntry(observation, scope)) return false;

    return calibration.countableSpecies === false
        || calibration.listEntry?.countable === false
        || calibration.listEntry?.exotic?.toLowerCase() === 'escapee';
}

function getDisplayTaxonomicOrder(observation: EBirdCalibratedObservation) {
    return observation.reportAsTaxonomicOrder
        ?? observation.canonicalTaxonomicOrder
        ?? observation.taxonomicOrder;
}

function getDisplayCommonName(observation: EBirdCalibratedObservation) {
    return observation.reportAsCommonName
        ?? observation.commonName;
}

export function getCalibratedDisplayObservationsBySpecies(
    calibrationResult: EBirdSightingsCalibrationResult,
    scope: CalibrationScope = 'life'
): EBirdObservationsBySpecies[] {
    const speciesList: EBirdObservationsBySpecies[] = [];
    const observations = scope === 'life'
        ? calibrationResult.observations
        : calibrationResult.observations.filter(observation => observation.observedYear === scope);

    for (const observation of observations) {
        const calibration = getObservationCalibration(observation, scope);
        if (!calibration?.countableSpecies) continue;

        const taxonomicOrder = getDisplayTaxonomicOrder(observation);
        const foundSpecies = speciesList.find(species => species.taxonomicOrder === taxonomicOrder);

        if (foundSpecies) {
            foundSpecies.observations.push(observation);
        } else {
            speciesList.push({
                taxonomicOrder,
                commonName: getDisplayCommonName(observation),
                observations: [observation]
            });
        }
    }

    return speciesList;
}
